import Config from 'src/config';
import { HelpAgentRemoteRegistry } from './helpAgentRemote.types';

const WIDGET_NAME = 'help-agent-app';
const LOAD_TIMEOUT_MS = 10000;
const REMOTE_SCRIPT_ATTRIBUTE = 'data-enlil-help-agent-remote';
type LoadMode = 'initial' | 'retry';

function resolveSameOriginWidgetUrl (widgetPath: string, origin: string): string {
  if (!widgetPath.startsWith('/') || widgetPath.startsWith('//')) {
    throw new TypeError('AI Help widget URL must be a root-relative path');
  }

  const widgetUrl = new URL(widgetPath, origin);
  if ((widgetUrl.protocol !== 'http:' && widgetUrl.protocol !== 'https:') || widgetUrl.origin !== origin) {
    throw new TypeError('AI Help widget URL must be a root-relative path');
  }

  return widgetUrl.href;
}

interface HelpAgentRemoteTimers {
  setTimeout: (callback: () => void, delay: number) => number
  clearTimeout: (timer: number) => void
}

export interface HelpAgentRemoteLoaderDependencies {
  document: Pick<Document, 'createElement' | 'head'>
  registry: HelpAgentRemoteRegistry
  timers: HelpAgentRemoteTimers
  widgetUrl: string
}

type HelpAgentRemoteLoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready' }
  | { status: 'error', error: Error };

export class HelpAgentRemoteLoader {
  private loading: Promise<void> | null = null;
  private loadState: HelpAgentRemoteLoadState = { status: 'idle' };
  private retrySequence = 0;
  private readonly widgetUrl: URL;

  public constructor (private readonly dependencies: HelpAgentRemoteLoaderDependencies) {
    try {
      this.widgetUrl = new URL(dependencies.widgetUrl);
    } catch {
      throw new TypeError('widgetUrl must be an absolute URL');
    }
  }

  public get state (): HelpAgentRemoteLoadState {
    return this.loadState;
  }

  public load (): Promise<void> {
    return this.startLoad('initial');
  }

  public retry (): Promise<void> {
    return this.startLoad(this.loadState.status === 'error' ? 'retry' : 'initial');
  }

  private startLoad (mode: LoadMode): Promise<void> {
    if (this.loading !== null) {
      return this.loading;
    }

    if (this.isRegistered()) {
      this.loadState = { status: 'ready' };
      return Promise.resolve();
    }

    if (this.loadState.status === 'error' && mode === 'initial') {
      return Promise.reject(this.loadState.error);
    }

    this.loadState = { status: 'loading' };
    const widgetUrl = mode === 'retry' ? this.nextRetryUrl() : this.widgetUrl.href;
    this.loading = this.loadRemote(widgetUrl).then(
      () => {
        this.loadState = { status: 'ready' };
      },
      (error: unknown) => {
        const failure = error instanceof Error ? error : new Error('Failed to load AI Help widget');
        this.loadState = { status: 'error', error: failure };
        throw failure;
      },
    ).finally(() => {
      this.loading = null;
    });
    return this.loading;
  }

  private isRegistered (): boolean {
    return this.dependencies.registry.get(WIDGET_NAME) !== undefined;
  }

  private loadRemote (widgetUrl: string): Promise<void> {
    const script = this.dependencies.document.createElement('script');
    script.type = 'module';
    script.src = widgetUrl;
    script.async = true;
    script.setAttribute(REMOTE_SCRIPT_ATTRIBUTE, '');
    this.dependencies.document.head.append(script);

    return new Promise<void>((resolve, reject) => {
      let scriptTimer: number | null = this.dependencies.timers.setTimeout(() => {
        fail(new Error('Timed out loading AI Help widget'));
      }, LOAD_TIMEOUT_MS);

      const cleanup = (): void => {
        if (scriptTimer !== null) {
          this.dependencies.timers.clearTimeout(scriptTimer);
          scriptTimer = null;
        }
        script.onload = null;
        script.onerror = null;
      };

      const fail = (error: Error): void => {
        cleanup();
        script.remove();
        reject(error);
      };

      script.onerror = (): void => {
        fail(new Error('Failed to load AI Help widget'));
      };
      script.onload = (): void => {
        cleanup();
        this.waitForRegistration().then(resolve, fail);
      };
    });
  }

  private nextRetryUrl (): string {
    const url = new URL(this.widgetUrl.href);
    url.searchParams.set('__enlil_retry', (++this.retrySequence).toString());
    return url.toString();
  }

  private waitForRegistration (): Promise<void> {
    if (this.isRegistered()) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      let registrationTimer: number | null = this.dependencies.timers.setTimeout(() => {
        fail(new Error('Timed out registering AI Help widget'));
      }, LOAD_TIMEOUT_MS);

      const complete = (): void => {
        if (registrationTimer !== null) {
          this.dependencies.timers.clearTimeout(registrationTimer);
          registrationTimer = null;
        }
      };

      const fail = (error: Error): void => {
        complete();
        reject(error);
      };

      this.dependencies.registry.whenDefined(WIDGET_NAME).then(() => {
        complete();
        resolve();
      }, (error: unknown) => {
        fail(error instanceof Error ? error : new Error('Failed to register AI Help widget'));
      });
    });
  }
}

export const helpAgentRemoteLoader = new HelpAgentRemoteLoader({
  document,
  registry: globalThis.customElements as HelpAgentRemoteRegistry,
  timers: {
    setTimeout: window.setTimeout.bind(window),
    clearTimeout: window.clearTimeout.bind(window),
  },
  widgetUrl: resolveSameOriginWidgetUrl(Config.AiHelpWidgetUrl, window.location.origin),
});
